import { useEffect } from 'react';
import { AccessibilityInfo } from 'react-native';
import Animated from 'react-native-reanimated';
import { useStore } from 'zustand';

import { SearchingStatementRow } from './SearchingStatementRow';
import type { ExtractedStatement } from '../../model/extractedStatements';
import { getStatementTriageStore } from '../../state/statementTriageStore';

import { ScrollView } from '@/components/ui/scroll-view';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { MOTION } from '@/constants/motion';
import { useEntranceAnimation } from '@/hooks/useEntranceAnimation';
import { useStaggeredEntrance } from '@/hooks/useStaggeredEntrance';

interface SearchingStatementListProps {
  messageSid: string;
  extractedStatements: ExtractedStatement[];
}

/**
 * Rendered inside `StatementTriageSheet` during the `searching`
 * wizard phase. Filters the full extracted-statements array down to
 * the indices the expert just submitted (`submittedIndices`) and
 * renders one `SearchingStatementRow` per kept statement.
 *
 * The blurb fades in (`MOTION.entrance.subtle`) and the rows enter
 * with a sequential stagger (`MOTION.entrance.sequential` +
 * `MOTION.stagger.standard`) so the wall builds up rather than
 * appearing all at once. This makes the `selection → searching`
 * transition feel like the unchecked rows fell away to reveal the
 * remaining work, even though the phase swap technically unmounts
 * the entire selection list.
 */
export function SearchingStatementList({
  messageSid,
  extractedStatements,
}: SearchingStatementListProps) {
  const store = getStatementTriageStore(messageSid);

  const submittedIndices = useStore(store, (state) => state.submittedIndices);

  /** Snaps every row's bar to a full 100 % in lockstep once the backend
   * reports the search complete. */
  const searchComplete = useStore(store, (state) => state.searchComplete);

  /** True once the search has run past the wizard's max-wait budget with
   * no completion signal — swaps the blurb to explain the delay. */
  const searchTimedOut = useStore(store, (state) => state.searchTimedOut);

  // The blurb's `accessibilityLiveRegion` covers Android/web, but iOS
  // VoiceOver ignores it — announce the timeout copy explicitly so an
  // iOS screen-reader user who has moved past the blurb still learns the
  // search is running long and can reach "Notify me when ready".
  useEffect(() => {
    if (searchTimedOut) {
      AccessibilityInfo.announceForAccessibility(
        "This search is taking longer than usual. You can keep waiting, or tap Notify me when ready and we'll text you when your references are ready."
      );
    }
  }, [searchTimedOut]);

  const blurbStyle = useEntranceAnimation({ ...MOTION.entrance.subtle, delay: 0 });

  const rowStyles = useStaggeredEntrance({
    count: submittedIndices.length,
    ...MOTION.entrance.sequential,
    ...MOTION.stagger.standard,
    baseDelay: 80,
  });

  return (
    <ScrollView className="max-h-[60vh] w-full">
      <VStack className="w-full px-4 pb-4 pt-2" space="sm">
        <Animated.View style={blurbStyle} accessibilityLiveRegion="polite">
          <Text size="sm" className="px-1 text-typography-500">
            {searchTimedOut
              ? "This search is taking longer than usual. You can keep waiting, or tap Notify me when ready and we'll text you the moment your references are ready."
              : "Primum is searching the literature for supporting references. This usually takes 20–30 seconds — you can stay here for the review or close this and we'll keep working in the background."}
          </Text>
        </Animated.View>
        {submittedIndices.map((statementIndex, rowOrder) => {
          const statement = extractedStatements[statementIndex];

          if (!statement) {
            return null;
          }

          return (
            <Animated.View key={`searching-row-${statementIndex}`} style={rowStyles[rowOrder]}>
              <SearchingStatementRow
                statement={statement}
                statementIndex={statementIndex}
                isComplete={searchComplete}
              />
            </Animated.View>
          );
        })}
      </VStack>
    </ScrollView>
  );
}
